import * as actions from '../../actions/layoutActions';
import {
    buildReducer,
    deletedItemsLeftNavItemId,
    contactListsLeftNavItemId,
} from '../../../shared';
import { LayoutState, Sorting, Filter } from '../../state/view/layoutState';
import * as configurationActions from '../../actions/coreActions';
import { parsePeopleHubOptions } from '../../utils/peopleHubOptionsParser';
import {
    enableCAPIv3ToggledAction,
    toggleLeftNavClickedAction,
    menuEntryClickedAction,
    confirmDeleteFolderClickedAction,
    setOverlayFolderPaneViewAction,
} from '../../../appchrome';
import {
    personaListFilterChangedAction,
    personaListSortingChangedAction,
    personaListDisplayOrderChangedAction,
    contactEditorNewContactSaveClickedAction,
} from '../../../hub';
import { privateDLCreatedAction } from '../../../privatedlcreateedit';
import { importContactsUploadFileCompletedAction } from '../../../importcontacts/actions';
import DisplayOrder from 'owa-people-types/lib/types/DisplayOrder';
import { getBrowserWidthBucket } from 'owa-layout';
import { shouldShowFolderPaneAsOverlay } from '../../../shared/utils';
import ContactsAPIClient from 'owa-contacts-api';

const defaultState: LayoutState = {
    leftPaneWidth: 260,
    listWidth: 350,
    isLeftNavVisible: true,
    displayOrder: DisplayOrder.FirstNameLastName,
    filter: Filter.All,
    sorting: Sorting.ByFirstName,
    deletedItemsSorting: Sorting.ByDeletedDate,
    contactListsSorting: Sorting.ByName,
    browserWidthBucket: getBrowserWidthBucket(),
    showHubList: true,
    showRightPane: true,
    showOverlayFolderPane: false,
    isTablet: shouldShowFolderPaneAsOverlay(getBrowserWidthBucket()) ? true : false,
    isCAPIv3Enabled: undefined,
};

export default buildReducer<LayoutState>()
    .handle(actions.getWidthOnWindowResizeCompletedAction, (state, action) => ({
        ...state,
        browserWidthBucket: action.payload.BrowserWidthBucket,
    }))
    .handle(actions.updateRightPaneStatusCompletedAction, (state, action) => ({
        ...state,
        showRightPane: action.payload.showRightPane,
    }))
    .handle(actions.updateshowHublistStatusCompletedAction, (state, action) => ({
        ...state,
        showHubList: action.payload.showHubList,
    }))
    .handle(personaListDisplayOrderChangedAction, (state, action) => ({
        ...state,
        displayOrder: action.payload.displayOrder,
    }))
    .handle(personaListFilterChangedAction, (state, action) => ({
        ...state,
        filter: action.payload.filter,
    }))
    .handle(personaListSortingChangedAction, (state, action) => {
        const isInDeletedItems = action.payload.activeLeftNavItemId === deletedItemsLeftNavItemId;
        const isInContactLists = action.payload.activeLeftNavItemId === contactListsLeftNavItemId;
        return {
            ...state,
            sorting:
                action.payload.isSearchMode || isInDeletedItems || isInContactLists
                    ? state.sorting
                    : action.payload.sorting,
            deletedItemsSorting: isInDeletedItems
                ? action.payload.sorting
                : state.deletedItemsSorting,
            contactListsSorting: isInContactLists
                ? action.payload.sorting
                : state.contactListsSorting,
        };
    })
    .handle(configurationActions.userConfigurationReceivedAction, (state, action) => {
        const peopleHubOptions = parsePeopleHubOptions(action.payload.userConfig);
        if (!peopleHubOptions) {
            return {
                ...state,
                isCAPIv3Enabled: ContactsAPIClient.isEnabled() || state.isCAPIv3Enabled,
            };
        }

        // Boolean and enumeration values must be checked if undefined, as they may have values which are evaluated as false.
        return {
            ...state,
            displayOrder:
                peopleHubOptions.displayOrder === undefined
                    ? state.displayOrder
                    : peopleHubOptions.displayOrder,
            filter: state.filter,
            isLeftNavVisible:
                peopleHubOptions.isLeftNavVisible === undefined
                    ? state.isLeftNavVisible
                    : peopleHubOptions.isLeftNavVisible,
            leftPaneWidth: peopleHubOptions.leftPaneWidth || state.leftPaneWidth,
            listWidth: peopleHubOptions.listWidth || state.listWidth,
            deletedItemsSorting:
                peopleHubOptions.deletedItemsSorting === undefined
                    ? state.deletedItemsSorting
                    : peopleHubOptions.deletedItemsSorting,
            contactListsSorting:
                peopleHubOptions.contactListsSorting === undefined
                    ? state.contactListsSorting
                    : peopleHubOptions.contactListsSorting,
            sorting:
                peopleHubOptions.sorting === undefined ? state.sorting : peopleHubOptions.sorting,
            // We want to default the setting to true in case the user has not initialized the value.
            // If the capiv3 flight is off, we want to go back to the initial state of this setting being undefined.
            isCAPIv3Enabled: ContactsAPIClient.isEnabled()
                ? peopleHubOptions.isCAPIv3Enabled || peopleHubOptions.isCAPIv3Enabled === undefined
                : state.isCAPIv3Enabled,
        };
    })
    .handle(actions.leftPaneWidthUpdatedAction, (state, action) => ({
        ...state,
        leftPaneWidth: action.payload.newWidth,
    }))
    .handle(actions.listWidthUpdatedAction, (state, action) => ({
        ...state,
        listWidth: action.payload.newWidth,
    }))
    .handle(toggleLeftNavClickedAction, state => ({
        ...state,
        isLeftNavVisible: !state.isLeftNavVisible,
    }))
    .handle(menuEntryClickedAction, state => ({
        ...state,
        filter: defaultState.filter,
    }))
    .handle(confirmDeleteFolderClickedAction, state => ({
        ...state,
        filter: defaultState.filter,
    }))
    .handle(contactEditorNewContactSaveClickedAction, (state, action) => ({
        ...state,
        filter: defaultState.filter,
    }))
    .handle(privateDLCreatedAction, state => ({
        ...state,
        filter: defaultState.filter,
    }))
    .handleDataAction(importContactsUploadFileCompletedAction, state => ({
        ...state,
        filter: defaultState.filter,
    }))
    .handle(setOverlayFolderPaneViewAction, (state, action) => ({
        ...state,
        showOverlayFolderPane:
            action.payload.showOverlayFolderPane === undefined
                ? state.isLeftNavVisible
                : action.payload.showOverlayFolderPane,
    }))
    .handle(actions.updateLeftNavStateOnResize, (state, action) => ({
        ...state,
        isLeftNavVisible: action.payload.leftNavVisible,
    }))
    .handle(actions.updateTabletFlagStatus, (state, action) => ({
        ...state,
        isTablet: action.payload.isTablet,
    }))
    .handle(enableCAPIv3ToggledAction, (state, action) => ({
        ...state,
        isCAPIv3Enabled: action.payload.isEnabled,
    }))
    .done(defaultState);
export type { Action } from '../../../shared';
